import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const MCP_ENDPOINT = "https://backend-production-cfa1f.up.railway.app/mcp";

const COLORS = {
  burgundy: "#5B0E14",
  gold: "#F1E194",
  cream: "#FDF6E3",
  goldLight: "#F1E19430",
  burgundyLight: "#5B0E1410",
};

const AI_CLIENTS = [
  {
    key: "claude_code",
    label: "Claude Code",
    icon: "🟣",
    color: "#7C3AED",
    configFilename: "claude_mcp_config.json",
    steps: [
      "افتح Terminal في مجلد مشروعك",
      `شغّل الأمر التالي (استبدل YOUR_TOKEN بمفتاحك):`,
      { code: `claude mcp add-json hujjah '{"type":"streamable-http","url":"${MCP_ENDPOINT}","headers":{"Authorization":"Bearer YOUR_TOKEN"}}' --scope user` },
      `تحقق من الإضافة: claude mcp list`,
      `الآن في كل محادثة Claude Code ستجد أدوات حُجّة متاحة تلقائياً`,
    ],
    buildConfig: (token) => ({
      mcpServers: {
        hujjah: {
          type: "streamable-http",
          url: MCP_ENDPOINT,
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    }),
  },
  {
    key: "cursor",
    label: "Cursor",
    icon: "🟢",
    color: "#10B981",
    configFilename: "cursor_mcp_config.json",
    steps: [
      "افتح Cursor → Settings (Ctrl+,) → البحث عن MCP",
      "اضغط على Add MCP Server",
      `أدخل الـ URL: ${MCP_ENDPOINT}`,
      "اختر النوع: HTTP / Streamable HTTP",
      "أضف Header: Authorization = Bearer YOUR_TOKEN",
      "احفظ وأعد تشغيل Cursor",
    ],
    buildConfig: (token) => ({
      mcpServers: {
        hujjah: {
          url: MCP_ENDPOINT,
          type: "http",
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    }),
  },
  {
    key: "vscode",
    label: "VS Code",
    icon: "🔵",
    color: "#3B82F6",
    configFilename: "vscode_mcp_settings.json",
    steps: [
      "ثبّت امتداد Continue (continue.dev) أو Copilot Chat",
      "افتح settings.json (Ctrl+Shift+P → Open User Settings JSON)",
      "أضف الكود التالي داخل الـ JSON:",
      {
        code: `"continue.mcpServerUrl": "${MCP_ENDPOINT}",\n"continue.mcpServerHeaders": {"Authorization": "Bearer YOUR_TOKEN"}`,
      },
      "احفظ الملف وأعد تحميل VS Code",
    ],
    buildConfig: (token) => ({
      "continue.mcpServerUrl": MCP_ENDPOINT,
      "continue.mcpServerHeaders": { Authorization: `Bearer ${token}` },
    }),
  },
  {
    key: "codex",
    label: "Codex CLI",
    icon: "⚫",
    color: "#374151",
    configFilename: "codex_mcp_config.json",
    steps: [
      "ثبّت Codex CLI من OpenAI:",
      { code: "npm install -g @openai/codex" },
      "شغّل Codex مع إضافة MCP server مباشرة:",
      {
        code: `codex --mcp-server '{"name":"hujjah","url":"${MCP_ENDPOINT}","headers":{"Authorization":"Bearer YOUR_TOKEN"}}'`,
      },
      "أو أضفه بشكل دائم في ملف الإعداد ~/.codex/config.json:",
      {
        code: `{\n  "mcpServers": [\n    {\n      "name": "hujjah",\n      "url": "${MCP_ENDPOINT}",\n      "headers": { "Authorization": "Bearer YOUR_TOKEN" }\n    }\n  ]\n}`,
      },
      "الآن Codex يعرف أدوات حُجّة ويستطيع إنشاء الأسئلة وإدارة المحتوى",
    ],
    buildConfig: (token) => ({
      mcpServers: [
        {
          name: "hujjah",
          url: MCP_ENDPOINT,
          headers: { Authorization: `Bearer ${token}` },
        },
      ],
    }),
  },
  {
    key: "windsurf",
    label: "Windsurf",
    icon: "🟠",
    color: "#F97316",
    configFilename: "windsurf_mcp_config.json",
    steps: [
      "افتح Windsurf → Settings → MCP Servers",
      "اضغط + لإضافة server جديد",
      `أدخل الـ URL: ${MCP_ENDPOINT}`,
      "نوع الاتصال: HTTP",
      "Authorization Header: Bearer YOUR_TOKEN",
      "احفظ وأعد تشغيل Windsurf",
    ],
    buildConfig: (token) => ({
      mcpServers: {
        hujjah: {
          url: MCP_ENDPOINT,
          type: "http",
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    }),
  },
];

function timeAgo(isoStr) {
  if (!isoStr) return null;
  try {
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return "الآن";
    if (diff < 3600) return `منذ ${Math.floor(diff / 60)} دقيقة`;
    if (diff < 86400) return `منذ ${Math.floor(diff / 3600)} ساعة`;
    return `منذ ${Math.floor(diff / 86400)} يوم`;
  } catch {
    return null;
  }
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
  toast.success("تم النسخ");
}

function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Token Modal ──────────────────────────────────────────────────────────── */
function NewTokenModal({ token: newToken, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" style={{ borderTop: `4px solid ${COLORS.burgundy}` }}>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">🔑</span>
          <div>
            <h3 className="font-black text-lg" style={{ color: COLORS.burgundy }}>مفتاح API الجديد</h3>
            <p className="text-sm text-gray-500">احفظه الآن — لن يُعرض مرة أخرى</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mb-4 text-sm text-amber-800 font-bold">
          ⚠️ هذا المفتاح يظهر مرة واحدة فقط. انسخه واحفظه في مكان آمن.
        </div>

        <div className="bg-gray-900 rounded-xl p-4 mb-4 flex items-center gap-3">
          <code className="text-green-400 text-xs font-mono flex-1 break-all select-all">{newToken}</code>
          <button
            onClick={() => copyToClipboard(newToken)}
            className="flex-shrink-0 text-white bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg text-xs font-bold transition-all">
            📋 نسخ
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl font-black text-white transition-all"
          style={{ background: COLORS.burgundy }}>
          فهمت — أغلق
        </button>
      </div>
    </div>
  );
}

/* ── Client Modal ─────────────────────────────────────────────────────────── */
function ClientModal({ client, keyPrefix, onClose }) {
  const [activeTab, setActiveTab] = useState("setup");
  const displayToken = keyPrefix || "YOUR_TOKEN";

  const handleDownload = () => {
    const config = client.buildConfig(displayToken);
    downloadJson(config, client.configFilename);
    toast.success("تم تحميل ملف الإعداد");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{client.icon}</span>
            <div>
              <h3 className="font-black text-lg" style={{ color: COLORS.burgundy }}>ربط {client.label}</h3>
              <p className="text-xs text-gray-400">MCP Content Server — حُجّة</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-all">✕</button>
        </div>

        <div className="p-5">
          <div className="flex gap-2 mb-5">
            {[
              { key: "setup", label: "الإعداد" },
              { key: "steps", label: "خطوات الربط" },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className="px-4 py-2 rounded-full text-sm font-bold transition-all"
                style={{
                  background: activeTab === t.key ? COLORS.burgundy : COLORS.burgundyLight,
                  color: activeTab === t.key ? "#fff" : COLORS.burgundy,
                }}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "setup" && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div>
                  <div className="text-xs font-black text-gray-500 mb-1">MCP Endpoint</div>
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <code className="text-sm font-mono text-gray-800 flex-1 break-all">{MCP_ENDPOINT}</code>
                    <button onClick={() => copyToClipboard(MCP_ENDPOINT)} className="text-gray-400 hover:text-gray-700 text-sm transition-colors">📋</button>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-black text-gray-500 mb-1">Token (prefix)</div>
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <code className="text-sm font-mono text-gray-800 flex-1">{displayToken}</code>
                    <button onClick={() => copyToClipboard(displayToken)} className="text-gray-400 hover:text-gray-700 text-sm transition-colors">📋</button>
                  </div>
                  {!keyPrefix && (
                    <p className="text-xs text-amber-600 mt-1">أنشئ مفتاح API أولاً لاستخدامه هنا</p>
                  )}
                </div>
                <div>
                  <div className="text-xs font-black text-gray-500 mb-1">نوع الاتصال</div>
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <code className="text-sm font-mono text-gray-800">streamable-http</code>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownload}
                className="w-full py-3 rounded-xl font-black text-sm transition-all flex items-center justify-center gap-2"
                style={{ background: COLORS.goldLight, color: COLORS.burgundy, border: `1px solid ${COLORS.gold}` }}>
                📥 تحميل ملف الإعداد ({client.configFilename})
              </button>
            </div>
          )}

          {activeTab === "steps" && (
            <div className="space-y-3">
              {client.steps.map((step, i) =>
                typeof step === "object" && step.code ? (
                  <div key={i} className="relative">
                    <pre className="bg-gray-900 text-green-400 rounded-xl p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap">{step.code.replace("YOUR_TOKEN", displayToken)}</pre>
                    <button
                      onClick={() => copyToClipboard(step.code.replace("YOUR_TOKEN", displayToken))}
                      className="absolute top-2 left-2 text-gray-400 hover:text-white text-xs px-2 py-1 rounded bg-gray-700 transition-all">
                      📋
                    </button>
                  </div>
                ) : (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-black text-white"
                      style={{ background: client.color }}>
                      {i + 1}
                    </div>
                    <p className="text-sm text-gray-700 pt-0.5">{step}</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ───────────────────────────────────────────────────────── */
export default function AIConnectionsPanel({ token, currentUser, isSuperAdmin }) {
  const [staffInfo, setStaffInfo] = useState(null);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [docsExpanded, setDocsExpanded] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadMyInfo();
  }, []);

  const loadMyInfo = async () => {
    setLoadingStaff(true);
    try {
      const { data } = await axios.get(`${API}/admin/staff/me`, { headers });
      setStaffInfo(data || null);
    } catch {
      toast.error("خطأ في تحميل بيانات الموظف");
    }
    setLoadingStaff(false);
  };

  const handleGenerateKey = async () => {
    if (!staffInfo?.id) return;
    if (!window.confirm("سيتم إنشاء مفتاح API جديد. أي مفتاح سابق سيتوقف عن العمل. هل تريد المتابعة؟")) return;
    setGeneratingKey(true);
    try {
      const { data } = await axios.post(`${API}/admin/staff/${staffInfo.id}/api-key`, {}, { headers });
      setNewToken(data.key || data.api_key);
      loadMyInfo();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل إنشاء المفتاح");
    }
    setGeneratingKey(false);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/mcp/test-connection`, {}, { headers });
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setTesting(false);
  };

  const handleRunDemo = async () => {
    setRunning(true);
    setDemoResult(null);
    try {
      const { data } = await axios.post(`${API}/mcp/run-demo`, {}, { headers });
      setDemoResult(data);
    } catch (e) {
      setDemoResult({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setRunning(false);
  };

  const isConnected = staffInfo?.api_key_active === true;
  const keyPrefix = staffInfo?.api_key_prefix || "";

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" dir="rtl">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-2xl font-black" style={{ color: COLORS.burgundy }}>🤖 AI Connections</h2>
          <p className="text-sm mt-1" style={{ color: COLORS.burgundy + "80" }}>
            ربط بيئات AI بخادم MCP — حُجّة
          </p>
        </div>
        <button
          onClick={loadMyInfo}
          className="px-4 py-2 rounded-full text-sm font-bold transition-all"
          style={{ background: COLORS.burgundyLight, color: COLORS.burgundy }}>
          ↻ تحديث
        </button>
      </div>

      {/* ── Section 1: Connection Status ──────────────────────────────────── */}
      <div className="rounded-2xl p-5" style={{ background: COLORS.cream, border: `1px solid ${COLORS.gold}` }}>
        <div className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: COLORS.burgundy + "60" }}>
          حالة الاتصال
        </div>
        {loadingStaff ? (
          <div className="text-center py-6 text-gray-400">⏳ جارٍ التحميل...</div>
        ) : (
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-md ${isConnected ? "bg-green-100" : "bg-red-50"}`}>
                {isConnected ? "✅" : "❌"}
              </div>
              <div>
                <div className="font-black text-xl" style={{ color: isConnected ? "#16a34a" : "#dc2626" }}>
                  {isConnected ? "متصل" : "غير متصل"}
                </div>
                <div className="text-sm text-gray-500">
                  {isConnected ? "مفتاح API نشط" : "لا يوجد مفتاح API نشط"}
                </div>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-2 gap-3 md:mr-8">
              <div className="bg-white rounded-xl p-3 text-center" style={{ border: `1px solid ${COLORS.gold}30` }}>
                <div className="text-2xl font-black" style={{ color: COLORS.burgundy }}>
                  {staffInfo?.api_key_request_count || 0}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">إجمالي الطلبات</div>
              </div>
              <div className="bg-white rounded-xl p-3 text-center" style={{ border: `1px solid ${COLORS.gold}30` }}>
                <div className="text-sm font-bold" style={{ color: COLORS.burgundy }}>
                  {timeAgo(staffInfo?.api_key_last_used) || "—"}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">آخر استخدام</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Section 2: My API Token ────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl p-5" style={{ border: `1px solid ${COLORS.gold}50` }}>
        <div className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: COLORS.burgundy + "60" }}>
          مفتاح API الخاص بي
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="text-gray-400 text-sm">🔑</span>
            {isConnected ? (
              <>
                <code className="font-mono text-sm text-gray-700 flex-1">{keyPrefix}</code>
                <button
                  onClick={() => copyToClipboard(keyPrefix)}
                  className="text-xs text-gray-400 hover:text-gray-700 transition-colors">
                  📋
                </button>
              </>
            ) : (
              <span className="text-gray-400 text-sm">لا يوجد مفتاح نشط</span>
            )}
          </div>
          <button
            onClick={handleGenerateKey}
            disabled={generatingKey || loadingStaff}
            className="px-4 py-3 rounded-xl text-sm font-black transition-all disabled:opacity-50 whitespace-nowrap"
            style={{ background: COLORS.burgundy, color: "#fff" }}>
            {generatingKey ? "⏳" : isConnected ? "🔄 تجديد المفتاح" : "✨ إنشاء مفتاح"}
          </button>
        </div>

        {isConnected && (
          <div className="text-xs text-gray-400 flex items-center gap-2">
            <span>أُنشئ:</span>
            <span>{staffInfo?.api_key_created_at ? new Date(staffInfo.api_key_created_at).toLocaleDateString("ar-SA") : "—"}</span>
            {staffInfo?.api_key_expires_at && (
              <>
                <span>·</span>
                <span>ينتهي: {new Date(staffInfo.api_key_expires_at).toLocaleDateString("ar-SA")}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Section 3: Quick Connect Cards ────────────────────────────────── */}
      <div>
        <div className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: COLORS.burgundy + "60" }}>
          ربط سريع
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {AI_CLIENTS.map((client) => (
            <button
              key={client.key}
              onClick={() => setSelectedClient(client)}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all hover:shadow-md hover:-translate-y-0.5 active:scale-95 text-center"
              style={{ background: COLORS.cream, borderColor: client.color + "40" }}>
              <span className="text-3xl">{client.icon}</span>
              <span className="text-xs font-black" style={{ color: client.color }}>{client.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Section 4: Test & Demo ─────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl p-5" style={{ border: `1px solid ${COLORS.gold}50` }}>
        <div className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: COLORS.burgundy + "60" }}>
          اختبار الاتصال
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="flex-1 py-3 rounded-xl font-black text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: COLORS.burgundyLight, color: COLORS.burgundy, border: `1px solid ${COLORS.burgundy}30` }}>
            {testing ? "⏳ جارٍ الاختبار..." : "🔌 Test Connection"}
          </button>
          <button
            onClick={handleRunDemo}
            disabled={running || !isConnected}
            className="flex-1 py-3 rounded-xl font-black text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: COLORS.goldLight, color: COLORS.burgundy, border: `1px solid ${COLORS.gold}` }}>
            {running ? "⏳ جارٍ التشغيل..." : "▶ Run Demo"}
          </button>
        </div>

        {testResult && (
          <div className={`rounded-xl p-4 mb-3 text-sm ${testResult.ok ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <div className="flex items-center gap-2 font-black mb-1" style={{ color: testResult.ok ? "#16a34a" : "#dc2626" }}>
              {testResult.ok ? "✅ الاتصال ناجح" : "❌ فشل الاتصال"}
              {testResult.latency_ms !== undefined && (
                <span className="font-normal text-gray-500">· {testResult.latency_ms}ms</span>
              )}
            </div>
            {testResult.ok ? (
              <div className="text-gray-600">عدد الأدوات المتاحة: <strong>{testResult.tools}</strong></div>
            ) : (
              <div className="text-red-600 font-mono text-xs">{testResult.error}</div>
            )}
          </div>
        )}

        {demoResult && (
          <div className={`rounded-xl p-4 text-sm ${demoResult.ok ? "bg-blue-50 border border-blue-200" : "bg-red-50 border border-red-200"}`}>
            <div className="flex items-center gap-2 font-black mb-2" style={{ color: demoResult.ok ? "#1d4ed8" : "#dc2626" }}>
              {demoResult.ok ? "✅ Demo نجح" : "❌ Demo فشل"}
              {demoResult.latency_ms !== undefined && (
                <span className="font-normal text-gray-500">· {demoResult.latency_ms}ms</span>
              )}
            </div>
            {demoResult.ok && demoResult.result && (
              <div className="space-y-1 text-gray-700">
                <div>أدوات متاحة: <strong>{demoResult.result.tools_available}</strong></div>
                <div className="text-xs text-gray-500">
                  عينة: {demoResult.result.sample?.join(", ")}
                </div>
              </div>
            )}
            {!demoResult.ok && (
              <div className="text-red-600 font-mono text-xs">{demoResult.error}</div>
            )}
          </div>
        )}
      </div>

      {/* ── Section 5: Documentation ───────────────────────────────────────── */}
      <div className="bg-white rounded-2xl overflow-hidden" style={{ border: `1px solid ${COLORS.gold}50` }}>
        <button
          onClick={() => setDocsExpanded(!docsExpanded)}
          className="w-full flex items-center justify-between p-5 text-right font-black transition-all"
          style={{ color: COLORS.burgundy }}>
          <span>📖 توثيق المنصة</span>
          <span className="text-gray-400">{docsExpanded ? "▲" : "▼"}</span>
        </button>

        {docsExpanded && (
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: "ما هو MCP؟",
                  content: "Model Context Protocol — بروتوكول يتيح لنماذج AI (Claude, GPT, إلخ) استخدام أدوات خارجية. خادم حُجّة يوفر أدوات لإدارة الأسئلة والفئات والمحتوى.",
                },
                {
                  title: "Claude Code",
                  content: "الأنسب للعمل في Terminal. يدعم streamable-http مباشرة. أضف الـ server مرة واحدة وسيكون متاحاً في كل المحادثات.",
                },
                {
                  title: "Cursor",
                  content: "محرر كود يدعم MCP عبر الإعدادات. بعد الإضافة يمكنك طلب من Cursor AI استخدام أدوات حُجّة مباشرة.",
                },
                {
                  title: "VS Code",
                  content: "عبر امتداد Continue أو Copilot Chat. يتطلب إضافة إعدادات في settings.json.",
                },
                {
                  title: "الأمان",
                  content: "كل مفتاح API مرتبط بموظف واحد. يُخزن مشفراً في قاعدة البيانات ولا يمكن استرداده بعد الإنشاء. يمكن إلغاؤه في أي وقت.",
                },
                {
                  title: "الأدوات المتاحة",
                  content: "إنشاء أسئلة، تعديل فئات، رفع وسائط، استعراض تقارير، تحليلات، وأكثر من 28 أداة.",
                },
              ].map((doc, i) => (
                <div key={i} className="rounded-xl p-4" style={{ background: COLORS.cream }}>
                  <div className="font-black text-sm mb-2" style={{ color: COLORS.burgundy }}>{doc.title}</div>
                  <p className="text-xs text-gray-600 leading-relaxed">{doc.content}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl p-4 bg-gray-900">
              <div className="text-xs font-black text-gray-400 mb-2">مثال: استخدام مفتاح API مع curl</div>
              <pre className="text-green-400 text-xs font-mono overflow-x-auto whitespace-pre">{`curl -X POST ${MCP_ENDPOINT}/tools/listCategories \\
  -H "Authorization: Bearer hujjah_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{}'`}</pre>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {newToken && <NewTokenModal token={newToken} onClose={() => setNewToken(null)} />}
      {selectedClient && (
        <ClientModal
          client={selectedClient}
          keyPrefix={keyPrefix}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </div>
  );
}
